import { useTranslation } from "react-i18next";
import { IndicatorIcon } from "src/ui/shared/IndicatorIcon";
import { ActivityIcon } from "src/ui/shared/Loading";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";
import { Tooltip } from "src/ui/shared/Tooltip";
import { makeStyles } from "tss-react/mui";

import { FileContentType, FileStatus } from "@dashboard/files/types/index";
import { hasAnalyzerInProgress, hasFailedAnalyzer } from "@dashboard/files/utils";
import { Theme } from "@mui/material";
import { Row } from "@tanstack/react-table";

import { FileExtendedTableItem } from "./UploadTable/UploadTable.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    warningsTooltipList: {
        paddingLeft: theme.spacing(5),
    },
    warningsTooltipItem: {
        marginTop: theme.spacing(1),
    },
    icon: {
        fontSize: 20,
        marginLeft: theme.spacing(1),
    },
    warning: {
        color: theme.palette.emteria.warning,
    },
    tooltipBox: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: 20,
        marginLeft: theme.spacing(1),
        fontSize: 20,
    },
}));

type IconInfo = {
    iconClass: string;
    tooltipKey: string;
};

const fileExtensionToIconMap: Record<string, IconInfo> = {
    doc: { iconClass: "fa-solid fa-file-word", tooltipKey: "text" },
    docx: { iconClass: "fa-solid fa-file-word", tooltipKey: "text" },
    exe: { iconClass: "fa-solid fa-windows", tooltipKey: "windowsExecutable" },
    zip: { iconClass: "fa-solid fa-file-zipper", tooltipKey: "archive" },
};

const contentTypeToIconMap: Record<string, IconInfo> = {
    [FileContentType.ImageJpeg]: { iconClass: "fa-solid fa-file-image", tooltipKey: "picture" },
    [FileContentType.ImagePng]: { iconClass: "fa-solid fa-file-image", tooltipKey: "picture" },
    [FileContentType.ImageGif]: { iconClass: "fa-solid fa-file-image", tooltipKey: "picture" },
    [FileContentType.TextPlain]: { iconClass: "fa-solid fa-file-lines", tooltipKey: "text" },
    [FileContentType.TextCsv]: { iconClass: "fa-solid fa-file-lines", tooltipKey: "text" },
    [FileContentType.Json]: { iconClass: "fa-solid fa-file-code", tooltipKey: "json" },
    [FileContentType.Zip]: { iconClass: "fa-solid fa-file-zipper", tooltipKey: "archive" },
    [FileContentType.ZipMultipart]: { iconClass: "fa-solid fa-file-zipper", tooltipKey: "archive" },
    [FileContentType.ZipCompressed]: { iconClass: "fa-solid fa-file-zipper", tooltipKey: "archive" },
    [FileContentType.ZipCompressedV2]: { iconClass: "fa-solid fa-file-zipper", tooltipKey: "archive" },
    [FileContentType.Apk]: { iconClass: "fa-brands fa-android", tooltipKey: "androidPackage" },
    [FileContentType.ApkV2]: { iconClass: "fa-brands fa-android", tooltipKey: "androidPackage" },
};

const getIconByFileName = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";
    return fileExtensionToIconMap[extension] || { iconClass: "fa-solid fa-file", tooltipKey: "unknownType" };
};

interface FileIconProps {
    row: Row<FileExtendedTableItem>;
}

const FileIcon = ({ row }: FileIconProps) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["pages"]);
    const analyzerInProgress = hasAnalyzerInProgress(row.original);
    const analyzerFailed = hasFailedAnalyzer(row.original);
    const isPending = row.original.status === FileStatus.Pending;
    const isProcessing = analyzerInProgress || isPending;
    const contentType = row.original.contentType ? contentTypeToIconMap[row.original.contentType] : null;
    const { iconClass, tooltipKey } = contentType || getIconByFileName(row.original.filename);
    let fileIconContent;

    const getTooltipText = (key: string) => {
        switch (key) {
            case "text":
                return t("pages:filesPage.tooltips.text");
            case "windowsExecutable":
                return t("pages:filesPage.tooltips.windowsExecutable");
            case "archive":
                return t("pages:filesPage.tooltips.archive");
            case "picture":
                return t("pages:filesPage.tooltips.picture");
            case "json":
                return t("pages:filesPage.tooltips.json");
            case "androidPackage":
                return t("pages:filesPage.tooltips.androidPackage");
            default:
                return t("pages:filesPage.tooltips.unknownType");
        }
    };

    if (row.original.warnings.length) {
        return (
            <MinimalTableCell>
                <Tooltip
                    placement="bottom"
                    title={
                        <ul className={classes.warningsTooltipList}>
                            {row.original.warnings.map((warning, index) => (
                                <li className={classes.warningsTooltipItem} key={index}>
                                    {warning}
                                </li>
                            ))}
                        </ul>
                    }
                >
                    <i className={cx("fa-solid fa-triangle-exclamation", classes.icon, classes.warning)} />
                </Tooltip>
            </MinimalTableCell>
        );
    }

    if (analyzerFailed) {
        fileIconContent = (
            <Tooltip placement="top" title={t("pages:filesPage.tooltips.analysisFailed")}>
                <i className={cx("fa-solid fa-triangle-exclamation", classes.icon, classes.warning)} />
            </Tooltip>
        );
    } else if (isProcessing) {
        fileIconContent = (
            <Tooltip placement="top" title={t("pages:filesPage.tooltips.processing")}>
                <div className={classes.tooltipBox}>
                    <ActivityIcon />
                </div>
            </Tooltip>
        );
    } else {
        fileIconContent = <IndicatorIcon icon={iconClass} tooltip={getTooltipText(tooltipKey)} className={classes.icon} />;
    }

    return (
        <MinimalTableCell>
            <div>{fileIconContent}</div>
        </MinimalTableCell>
    );
};

export default FileIcon;
